import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { ArrowLeft, Loader2, Download, FileText } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart } from 'recharts';
import html2pdf from 'html2pdf.js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NitroReportViewer = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const reportRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [imageBlobUrls, setImageBlobUrls] = useState({});

  useEffect(() => {
    fetchReport();
    return () => {
      // Cleanup blob URLs on unmount
      Object.values(imageBlobUrls).forEach(url => { if (url) window.URL.revokeObjectURL(url); });
    };
  }, [reportId]);

  // Fetch authenticated images as blob URLs
  useEffect(() => {
    if (!reportData) return;
    const imageKeys = [
      { key: 'financials', pathField: 'financials_screenshot_path', endpoint: 'financials-image' },
      { key: 'keyFinancials', pathField: 'key_financials_screenshot_path', endpoint: 'key-financials-image' },
      { key: 'earningsMultiples', pathField: 'earnings_multiples_screenshot_path', endpoint: 'earnings-multiples-image' },
    ];
    const fetchImages = async () => {
      const urls = {};
      for (const { key, pathField, endpoint } of imageKeys) {
        if (reportData[pathField]) {
          try {
            const rid = reportData.id || reportId;
            const response = await axios.get(`${API}/reports/nitro/${endpoint}/${rid}`, { responseType: 'blob', timeout: 30000 });
            urls[key] = window.URL.createObjectURL(new Blob([response.data]));
          } catch (e) {
            console.warn(`Failed to load ${key} image:`, e);
          }
        }
      }
      setImageBlobUrls(urls);
    };
    fetchImages();
  }, [reportData, reportId]);

  const fetchReport = async () => {
    try {
      const response = await axios.get(`${API}/reports/${reportId}`);
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!reportRef.current) return;
    
    try {
      toast.info('Generating PDF from report... This may take a moment.');
      
      const element = reportRef.current;
      
      const opt = {
        margin: 0,
        filename: `Nitro_Report_${reportId.substring(0, 8)}.pdf`,
        image: { type: 'jpeg', quality: 0.97 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          logging: false,
          letterRendering: true,
          scrollY: 0,
          windowWidth: 794,
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait' 
        },
        pagebreak: { 
          mode: ['css', 'legacy'],
          before: '[data-page-break]',
          avoid: ['tr', 'td'],
        }
      };
      
      await html2pdf().set(opt).from(element).save();
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-600 mb-4">Report not found</p>
          <Button onClick={() => navigate('/')}>Go Back</Button>
        </div>
      </div>
    );
  }

  const page1 = reportData.page_1 || {};
  const header = page1.header || {};
  const keyMetrics = page1.key_valuation_metrics || {};
  const jaadeValue = page1.jaade_carrying_value || {};
  const capTable = page1.cap_table || {};

  // Helper: format a raw number OR already-formatted string for display
  const fmtFinancial = (val, type = 'auto') => {
    if (val === null || val === undefined) return 'N/A';
    // If it's already a well-formatted string (contains $, %, m, commas), return as-is
    if (typeof val === 'string') {
      const trimmed = val.trim();
      if (trimmed === '' || trimmed === '0' || trimmed === '0.0') return type === 'pct' ? '0.0%' : trimmed;
      if (trimmed.includes('$') || trimmed.includes('%') || /[a-zA-Z]/.test(trimmed)) return trimmed;
      // Try to parse as number for unformatted strings
      const num = parseFloat(trimmed.replace(/,/g, ''));
      if (isNaN(num)) return trimmed;
      val = num;
    }
    if (typeof val === 'number') {
      if (isNaN(val)) return 'N/A';
      if (type === 'pct') return `${(val * 100).toFixed(1)}%`;
      if (type === 'pct_direct') return `${val.toFixed(1)}%`;
      if (type === 'price') return `$${val.toFixed(2)}`;
      if (type === 'millions') return `$${val.toFixed(1)}m`;
      if (type === 'shares_m') return `${val.toFixed(1)}m`;
      if (type === 'shares_count') return val.toLocaleString('en-AU', { maximumFractionDigits: 1 }) + 'm';
      if (type === 'change_pct') return `${val >= 0 ? '+' : ''}${val.toFixed(1)}%`;
      // auto: guess based on magnitude
      if (Math.abs(val) < 10) return `$${val.toFixed(2)}`;
      return `$${val.toFixed(1)}m`;
    }
    return String(val);
  };

  // Format key_valuation_metrics values properly
  const fmtKM = {
    share_price: fmtFinancial(keyMetrics.share_price, 'price'),
    shares_on_issue: (() => {
      const v = keyMetrics.shares_on_issue;
      if (typeof v === 'number') return `${v.toFixed(1)}m`;
      return v || 'N/A';
    })(),
    market_cap: fmtFinancial(keyMetrics.market_cap, 'millions'),
    net_debt_cash: fmtFinancial(keyMetrics.net_debt_cash, 'millions'),
    enterprise_value: fmtFinancial(keyMetrics.enterprise_value, 'millions'),
    change_in_share_price: (() => {
      const v = keyMetrics.change_in_share_price;
      if (v === null || v === undefined) return 'N/A';
      if (typeof v === 'number') return `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;
      if (typeof v === 'string' && v.trim() !== '') return v;
      return 'N/A';
    })(),
  };

  const fmtJV = {
    shares_held: (() => {
      const v = jaadeValue.shares_held;
      if (typeof v === 'number') return `${v.toFixed(1)}m`;
      return v || 'N/A';
    })(),
    carrying_value: fmtFinancial(jaadeValue.carrying_value, 'millions'),
    investment_irr: (() => {
      const v = jaadeValue.investment_irr;
      if (typeof v === 'number') {
        // If value > 1, assume it's already a percentage (e.g. 24.6)
        if (Math.abs(v) > 1) return `${v.toFixed(1)}%`;
        // Otherwise it's a decimal (e.g. 0.246 = 24.6%)
        return `${(v * 100).toFixed(1)}%`;
      }
      return v || 'N/A';
    })(),
  };
  
  // Helper function to safely extract text from string or object
  const extractText = (data) => {
    if (typeof data === 'string') {
      return data;
    }
    if (typeof data === 'number') {
      return String(data);
    }
    if (typeof data === 'object' && data !== null) {
      // Handle {value, unit, label} pattern from AI
      if (data.value !== undefined) {
        const unit = data.unit || '';
        return `${data.value}${unit}`;
      }
      if (data.text) return data.text;
      if (data.content) return data.content;
      if (data.summary) return data.summary;
      // If it has multiple text fields, combine them
      const textFields = [];
      if (data.summary) textFields.push(data.summary);
      if (data.assumptions) {
        if (Array.isArray(data.assumptions)) {
          textFields.push(...data.assumptions.map(a => typeof a === 'object' ? JSON.stringify(a) : String(a)));
        } else {
          textFields.push(String(data.assumptions));
        }
      }
      if (data.calculation_steps) {
        if (Array.isArray(data.calculation_steps)) {
          textFields.push(...data.calculation_steps.map(a => typeof a === 'object' ? JSON.stringify(a) : String(a)));
        } else {
          textFields.push(String(data.calculation_steps));
        }
      }
      if (data.adjustments_and_sensitivities) {
        textFields.push(String(data.adjustments_and_sensitivities));
      }
      if (textFields.length > 0) {
        return textFields.join('\n\n');
      }
      // Last resort: stringify the object
      return JSON.stringify(data, null, 2);
    }
    return '';
  };
  
  const companyOverview = extractText(page1.company_overview);
  const investmentThesis = extractText(page1.investment_thesis);
  const keyRisks = extractText(page1.key_risks);
  const outlook = extractText(page1.outlook);
  // recommendation can be a JSON object {text, fair_value_per_share, valuation_change} or legacy plain string
  const recObj = (typeof page1.recommendation === 'object' && page1.recommendation !== null) ? page1.recommendation : null;
  const recommendation = recObj ? (recObj.text || '') : extractText(page1.recommendation);
  const recFairValuePerShare = recObj?.fair_value_per_share || null;
  const recValuationChange = recObj?.valuation_change || null;
  const discountToComps = extractText(page1.discount_to_comps);
  const opPerf = extractText(page1.continue_strong_operating_performance);
  const precedentTx = extractText(page1.precedent_transactions);
  const lreMethod = page1.lre_methodology_table || {};
  const financials = page1.key_financials_table || {};

  // Page 4 data
  const page4 = reportData.page_4 || {};
  const assessmentFV = page4.assessment_of_fair_value || {};
  const reviewOF = page4.review_of_operational_factors || {};
  const financialPerf = page4.financial_performance || {};
  const valuationSummary = page4.valuation_summary || {};
  const earningsMultiples = page4.earnings_multiples || {};
  const financialsImageUrl = imageBlobUrls.financials || null;
  const keyFinancialsImageUrl = imageBlobUrls.keyFinancials || null;
  const earningsMultiplesImageUrl = imageBlobUrls.earningsMultiples || null;

  // Safe cell renderer for table values that might be objects {value, unit, label}
  const cell = (val) => {
    if (val === undefined || val === null) return '-';
    if (typeof val === 'string' || typeof val === 'number') return String(val);
    if (typeof val === 'object') {
      if (val.value !== undefined) {
        const v = String(val.value);
        // Skip template/eval expressions
        if (v.includes('{') || v.includes('eval(')) return '-';
        const unit = val.unit || '';
        return `${v}${unit}`;
      }
      return '-';
    }
    return '-';
  };

  // Reusable page header
  const PageHeader = () => (
    <div className="flex justify-between items-start mb-6 pb-4 border-b-2" style={{ borderColor: '#A8A67A' }}>
      <div>
        <h1 className="text-2xl font-bold mb-0.5" style={{ color: '#A8A67A' }}>
          {header.title || 'Ellerston JAADE'}
        </h1>
        <p className="text-sm text-slate-500">{header.subtitle || 'Ellerston Capital'}</p>
      </div>
      <div className="text-right">
        <p className="text-base font-semibold text-slate-700">{header.report_subject || `Nitro – Q${reportData.quarter} ${reportData.year}`}</p>
      </div>
    </div>
  );

  // A4 page wrapper: 210mm × 297mm
  const A4Page = ({ children, pageNum }) => (
    <div
      data-page-break
      className="bg-white shadow-xl mx-auto my-8 relative overflow-hidden"
      style={{
        width: '210mm',
        minHeight: '297mm',
        padding: '14mm 16mm 14mm 16mm',
        boxSizing: 'border-box',
        pageBreakBefore: pageNum > 1 ? 'always' : 'auto',
      }}
    >
      {children}
      <div className="absolute text-xs text-slate-400" style={{ bottom: '8mm', right: '12mm' }}>{pageNum}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Top Navigation */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Generator
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  toast.info('Generating Word from template...');
                  const response = await axios.get(`${API}/reports/nitro/${reportId}/docx-from-template`, {
                    responseType: 'blob', timeout: 60000,
                  });
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  const cd = response.headers['content-disposition'];
                  let fn = `Nitro_Template_${reportId.substring(0, 8)}.docx`;
                  if (cd) { const m = cd.match(/filename="?([^"]+)"?/); if (m) fn = m[1]; }
                  link.setAttribute('download', fn);
                  document.body.appendChild(link); link.click(); link.remove();
                  window.URL.revokeObjectURL(url);
                  toast.success('Template Word document downloaded');
                } catch (error) {
                  toast.error('Failed to generate template document');
                }
              }}
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              <FileText className="w-4 h-4 mr-2" />
              Download Word (Template)
            </Button>
            {/* PDF export button hidden — keep handleDownloadPDF for future use */}
          </div>
        </div>
      </div>

      {/* Report Pages */}
      <div className="py-4" ref={reportRef}>
        {/* ── PAGE 1 ──────────────────────────────────────────────── */}
        <A4Page pageNum={1}>
          <PageHeader />
          <div className="grid grid-cols-12 gap-5">
            {/* Left sidebar – 5 cols */}
            <div className="col-span-5 space-y-4 bg-slate-50 rounded-lg p-4">

              {/* Key Valuation Metrics */}
              <div>
                <h3 className="text-xs font-bold mb-2 uppercase tracking-wide" style={{ color: '#A8A67A' }}>
                  Key Valuation Metrics
                </h3>
                <table className="w-full text-xs">
                  <tbody>
                    <tr className="border-b">
                      <td className="py-1">Share Price</td>
                      <td className="text-right font-semibold">{fmtKM.share_price}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-1">Shares on Issue</td>
                      <td className="text-right">{fmtKM.shares_on_issue}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-1">Market Cap</td>
                      <td className="text-right">{fmtKM.market_cap}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-1">Net Debt (Cash)</td>
                      <td className="text-right">{fmtKM.net_debt_cash}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-1 font-semibold">Enterprise Value</td>
                      <td className="text-right font-bold">{fmtKM.enterprise_value}</td>
                    </tr>
                    <tr>
                      <td className="py-1">Chg in Share Price</td>
                      <td className="text-right">
                        <span className="font-bold text-red-600 border-2 border-red-500 px-1.5 py-0.5 rounded text-xs inline-block">
                          {fmtKM.change_in_share_price}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* JAADE Carrying Value */}
              <div>
                <h3 className="text-xs font-bold mb-2 uppercase tracking-wide" style={{ color: '#A8A67A' }}>
                  JAADE Carrying Value
                </h3>
                <table className="w-full text-xs">
                  <tbody>
                    <tr className="border-b">
                      <td className="py-1">Shares Held</td>
                      <td className="text-right">{fmtJV.shares_held}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-1 font-semibold">Carrying Value</td>
                      <td className="text-right font-bold">{fmtJV.carrying_value}</td>
                    </tr>
                    <tr>
                      <td className="py-1">Investment IRR</td>
                      <td className="text-right font-semibold">{fmtJV.investment_irr}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Cap Table */}
              <div>
                <h3 className="text-xs font-bold mb-2 uppercase tracking-wide" style={{ color: '#A8A67A' }}>
                  Cap Table
                </h3>
                <table className="w-full text-xs">
                  <tbody>
                    <tr className="border-b">
                      <td className="py-1">Potentia</td>
                      <td className="text-right">{capTable.potentia || (capTable.shares_held ? 'N/A' : 'N/A')}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-1">HVP</td>
                      <td className="text-right">{capTable.hvp || 'N/A'}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-1 font-semibold">Ellerston JAADE</td>
                      <td className="text-right font-semibold">{capTable.ellerston_jaade || 'N/A'}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-1">Others</td>
                      <td className="text-right">{capTable.others || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td className="py-1 font-bold">Total</td>
                      <td className="text-right font-bold">100%</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Company Overview */}
              <div>
                <h3 className="text-xs font-bold mb-2 uppercase tracking-wide" style={{ color: '#A8A67A' }}>
                  Company Overview
                </h3>
                <div className="text-xs leading-relaxed text-slate-700">
                  {companyOverview ? (
                    <div dangerouslySetInnerHTML={{ __html: companyOverview.replace(/\n/g, '<br />') }} />
                  ) : (
                    <p>Company overview content will appear here</p>
                  )}
                </div>
              </div>

              {/* Investment Thesis */}
              <div>
                <h3 className="text-xs font-bold mb-2 uppercase tracking-wide" style={{ color: '#A8A67A' }}>
                  Investment Thesis
                </h3>
                <div className="text-xs leading-relaxed text-slate-700">
                  {investmentThesis ? (
                    <div dangerouslySetInnerHTML={{ __html: investmentThesis.replace(/\n/g, '<br />') }} />
                  ) : (
                    <p>Investment thesis content will appear here</p>
                  )}
                </div>
              </div>
            </div>

            {/* Right column – 7 cols */}
            <div className="col-span-7 space-y-4">

              {/* Recommendation */}
              <div>
                <h3 className="text-sm font-bold mb-2" style={{ color: '#A8A67A' }}>
                  Recommendation
                </h3>
                {/* Fair value per share badge – sourced from Key Valuation Metrics for accuracy */}
                {fmtKM.share_price && fmtKM.share_price !== 'N/A' && (
                  <div className="bg-slate-50 p-3 rounded-lg border-2 border-slate-200 mb-3">
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold" style={{ color: '#A8A67A' }}>
                        {fmtKM.share_price.startsWith('$') ? fmtKM.share_price : `$${fmtKM.share_price}`}
                      </span>
                      <span className="text-sm font-semibold text-slate-600">per share</span>
                      {fmtKM.change_in_share_price && fmtKM.change_in_share_price !== 'N/A' && (
                        <span className={`text-xs font-semibold ml-2 ${String(fmtKM.change_in_share_price).startsWith('+') ? 'text-green-600' : String(fmtKM.change_in_share_price).startsWith('-') ? 'text-red-600' : 'text-slate-600'}`}>
                          {String(fmtKM.change_in_share_price).startsWith('+') ? '↑' : String(fmtKM.change_in_share_price).startsWith('-') ? '↓' : ''} {fmtKM.change_in_share_price}
                        </span>
                      )}
                    </div>
                  </div>
                )}
                <div className="text-xs leading-relaxed text-slate-700">
                  {recommendation ? (
                    <div dangerouslySetInnerHTML={{ __html: recommendation.replace(/\n/g, '<br />') }} />
                  ) : (
                    <p>Recommendation content will appear here</p>
                  )}
                </div>
              </div>

              {/* Discount to Public Comps */}
              <div>
                <h3 className="text-xs font-bold mb-2 uppercase tracking-wide" style={{ color: '#A8A67A' }}>
                  Discount to Public Comps
                </h3>
                <div className="text-xs leading-relaxed text-slate-700">
                  {discountToComps ? (
                    <div dangerouslySetInnerHTML={{ __html: discountToComps.replace(/\n/g, '<br />') }} />
                  ) : (
                    <p>Discount analysis will appear here</p>
                  )}
                </div>
              </div>

              {/* Continue Strong Operating Performance */}
              <div>
                <h3 className="text-xs font-bold mb-2 uppercase tracking-wide" style={{ color: '#A8A67A' }}>
                  Continue Strong Operating Performance
                </h3>
                <div className="space-y-2 text-xs leading-relaxed text-slate-700">
                  {opPerf ? (
                    <div dangerouslySetInnerHTML={{ __html: opPerf.replace(/\n/g, '<br />') }} />
                  ) : (
                    <p>Operating performance analysis will appear here</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </A4Page>

        {/* ── PAGE 2 – Thesis / Risks / Outlook + Precedent + LRE + Financials ── */}
        <A4Page pageNum={2}>
          <PageHeader />

          <div className="grid grid-cols-12 gap-5">
            {/* Left – Key Risks */}
            <div className="col-span-5 space-y-4 bg-slate-50 rounded-lg p-4">

              <div>
                <h3 className="text-xs font-bold mb-2 uppercase tracking-wide" style={{ color: '#A8A67A' }}>
                  Key Risks / Points to Monitor
                </h3>
                <div className="text-xs leading-relaxed text-slate-700">
                  {keyRisks ? (
                    <div dangerouslySetInnerHTML={{ __html: keyRisks.replace(/\n/g, '<br />') }} />
                  ) : (
                    <p>Key risks content will appear here</p>
                  )}
                </div>
              </div>
            </div>

            {/* Right – Outlook + Precedent Transactions + Fair Value Methodology */}
            <div className="col-span-7 space-y-4">

              <div>
                <h3 className="text-xs font-bold mb-2 uppercase tracking-wide" style={{ color: '#A8A67A' }}>
                  Outlook
                </h3>
                <div className="text-xs leading-relaxed text-slate-700">
                  {outlook ? (
                    <div dangerouslySetInnerHTML={{ __html: outlook.replace(/\n/g, '<br />') }} />
                  ) : (
                    <p>Outlook content will appear here</p>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-xs font-bold mb-2 uppercase tracking-wide" style={{ color: '#A8A67A' }}>
                  Valuation Supported by Precedent Transactions
                </h3>
                <div className="text-xs leading-relaxed text-slate-700">
                  {precedentTx ? (
                    <div dangerouslySetInnerHTML={{ __html: precedentTx.replace(/\n/g, '<br />') }} />
                  ) : (
                    <p>Precedent transactions analysis will appear here</p>
                  )}
                </div>
              </div>

              {/* Fair Value Methodology – LRE Table */}
              <div>
                <h3 className="text-xs font-bold mb-2 uppercase tracking-wide" style={{ color: '#A8A67A' }}>
                  Fair Value Methodology
                </h3>
                {lreMethod.intro_text && (
                  <p className="text-xs text-slate-600 mb-3 leading-relaxed italic">
                    {lreMethod.intro_text}
                  </p>
                )}
                {lreMethod.lre_table ? (
                  <div className="bg-slate-100 p-3 rounded-lg border-2" style={{ borderColor: '#A8A67A' }}>
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="text-xs font-bold">Long Run Earnings (in 4yrs)</h4>
                      <span className="text-xs font-semibold">A$</span>
                    </div>
                    <table className="w-full text-xs">
                      <tbody>
                        <tr className="border-b">
                          <td className="py-1">EV/NTM Revenue</td>
                          <td className="text-right font-semibold">{cell(lreMethod.lre_table.ev_ntm_revenue)}</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-1">Liquidity Discount</td>
                          <td className="text-right">{cell(lreMethod.lre_table.liquidity_discount)}</td>
                        </tr>
                        <tr className="border-b bg-white">
                          <td className="py-1 font-semibold">Deal multiple (x)</td>
                          <td className="text-right font-bold">{cell(lreMethod.lre_table.deal_multiple)}</td>
                        </tr>
                        <tr className="border-b bg-white">
                          <td className="py-1">Net Revenue – 4 Years ($m)</td>
                          <td className="text-right font-semibold">{cell(lreMethod.lre_table.net_revenue_year4)}</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-1 font-semibold">Enterprise Value ($m) – implied</td>
                          <td className="text-right font-bold">{cell(lreMethod.lre_table.enterprise_value_implied)}</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-1">EBITDA margin – Steady state</td>
                          <td className="text-right">{cell(lreMethod.lre_table.ebitda_margin_steady)}</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-1">Estimated EBITDA ($m)</td>
                          <td className="text-right">{cell(lreMethod.lre_table.estimated_ebitda)}</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-1 font-semibold">Implied EV/EBITDA</td>
                          <td className="text-right font-bold">{cell(lreMethod.lre_table.implied_ev_ebitda)}</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-1">Plus Adjusted Cash/(debt)</td>
                          <td className="text-right">{cell(lreMethod.lre_table.adjusted_cash_debt)}</td>
                        </tr>
                        <tr className="border-b border-slate-400">
                          <td className="py-1">Equity Valuation ($m)</td>
                          <td className="text-right">{cell(lreMethod.lre_table.equity_valuation_year4 || lreMethod.lre_table.equity_valuation)}</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-1 pt-2">Discount rate</td>
                          <td className="text-right pt-2">{cell(lreMethod.lre_table.discount_rate)}</td>
                        </tr>
                        <tr className="border-b-2 border-t-2 border-slate-600">
                          <td className="py-1 font-semibold">Equity Valuation ($m) – Today</td>
                          <td className="text-right font-bold">{cell(lreMethod.lre_table.equity_valuation_today)}</td>
                        </tr>
                        <tr className="border-b border-slate-400">
                          <td className="py-1">Net Debt ($m) – Today</td>
                          <td className="text-right">({cell(lreMethod.lre_table.net_debt_today)})</td>
                        </tr>
                        <tr className="border-b-2 border-t-2 border-slate-600">
                          <td className="py-1 font-semibold">Enterprise Value ($m) – Today</td>
                          <td className="text-right font-bold">{cell(lreMethod.lre_table.enterprise_value_today)}</td>
                        </tr>
                        <tr>
                          <td className="py-1 font-bold italic">Implied Share price ($)</td>
                          <td className="text-right font-bold text-sm" style={{ color: '#A8A67A' }}>
                            {cell(lreMethod.lre_table.implied_share_price)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-xs text-slate-600">Fair value methodology table will appear here</p>
                )}
              </div>
            </div>
          </div>

          {/* Key Financials & Valuation Multiples – bottom of page 2 */}
          <div className="mt-5 pt-4 border-t">
            <h3 className="text-xs font-bold mb-3 uppercase tracking-wide" style={{ color: '#A8A67A' }}>
              Key Financials &amp; Valuation Multiples
            </h3>
            {keyFinancialsImageUrl ? (
              <img
                src={keyFinancialsImageUrl}
                alt="Key Financials & Valuation Multiples"
                className="w-full rounded"
                style={{ maxHeight: '380px', objectFit: 'contain', objectPosition: 'left top' }}
              />
            ) : (
              <p className="text-xs text-slate-500 italic">
                No screenshot uploaded. Upload a Key Financials &amp; Valuation Multiples screenshot in Step 5 before generating the report.
              </p>
            )}
          </div>
        </A4Page>

        {/* ── PAGE 3 – Fair Value Conclusion ── */}
        <A4Page pageNum={3}>
          <PageHeader />
          <div>
            <h2 className="text-base font-bold mb-4" style={{ color: '#A8A67A' }}>
              Fair Value Conclusion
            </h2>
            {(() => {
              const fvcRaw = reportData.fair_value_conclusion_text || (reportData.page_2 && reportData.page_2.fair_value_conclusion);
              const fvcText = typeof fvcRaw === 'string' ? fvcRaw : '';
              if (!fvcText) {
                return (
                  <p className="text-xs text-slate-500 italic">
                    Fair Value Conclusion content will appear once the report is generated.
                  </p>
                );
              }
              // Split into paragraphs/bullets, render with proper formatting
              const lines = fvcText.split('\n').filter(l => l.trim());
              return (
                <div className="space-y-2">
                  {lines.map((line, idx) => {
                    const trimmed = line.trim();
                    // Detect bullet lines (start with -, *, Ø, •, ►, or similar)
                    const isBullet = /^[\-\*\u00D8\u2022\u25BA\u25B6\u25CF\u2013\u2014►Ø]/.test(trimmed);
                    // Detect sub-bullets (start with o followed by space, or indented with spaces before bullet)
                    const isSubBullet = /^\s{2,}[o\-\*]/.test(line) || /^o\s/.test(trimmed);
                    
                    if (isSubBullet) {
                      const cleanText = trimmed.replace(/^[o\-\*]\s*/, '').trim();
                      return (
                        <div key={idx} className="flex gap-2 items-start ml-8">
                          <span className="text-xs mt-0.5 flex-shrink-0 text-slate-400">○</span>
                          <p className="text-xs leading-relaxed text-slate-700">{cleanText}</p>
                        </div>
                      );
                    }
                    
                    if (isBullet) {
                      const cleanText = trimmed.replace(/^[\-\*\u00D8\u2022\u25BA\u25B6\u25CF\u2013\u2014►Ø]\s*/, '').trim();
                      return (
                        <div key={idx} className="flex gap-2 items-start">
                          <span className="text-xs font-bold mt-0.5 flex-shrink-0" style={{ color: '#A8A67A' }}>►</span>
                          <p className="text-xs leading-relaxed text-slate-700">{cleanText}</p>
                        </div>
                      );
                    }
                    
                    // Regular paragraph
                    return (
                      <p key={idx} className="text-xs leading-relaxed text-slate-700">{trimmed}</p>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </A4Page>

        {/* ── PAGE 4 – Trading Multiple Evolution ── */}
        <A4Page pageNum={4}>
          {(() => {
            const page2 = reportData.page_2 || {};
            const tradingMultiple = page2.trading_multiple_evolution || {};

            return (
              <>
                <PageHeader />

                <div>
                  <h2 className="text-base font-bold mb-3" style={{ color: '#A8A67A' }}>
                    Trading Multiple Evolution
                  </h2>

                  {tradingMultiple.chart_data && tradingMultiple.chart_data.length > 0 ? (
                    <div className="space-y-4">
                      <div className="bg-slate-50 p-4 rounded-lg" style={{ height: '340px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart data={tradingMultiple.chart_data} margin={{ top: 15, right: 25, left: 15, bottom: 15 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#64748b" />
                            <YAxis yAxisId="left" label={{ value: 'EV (A$m)', angle: -90, position: 'insideLeft', style: { fontSize: 10 } }} tick={{ fontSize: 10 }} stroke="#64748b" />
                            <YAxis yAxisId="right" orientation="right" label={{ value: 'EV/NTM Rev (x)', angle: 90, position: 'insideRight', style: { fontSize: 10 } }} tick={{ fontSize: 10 }} stroke="#64748b" />
                            <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '11px' }}
                              formatter={(value, name) => {
                                if (name === 'EV') return [`A$${value}m`, 'Enterprise Value'];
                                if (name === 'Nitro') return [`${value}x`, 'Nitro EV/NTM'];
                                if (name === 'Comps') return [`${value}x`, 'Public Comps'];
                                return [value, name];
                              }} />
                            <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }} />
                            <Bar yAxisId="left" dataKey="ev" fill="#A8A67A" name="EV" opacity={0.7} />
                            <Line yAxisId="right" type="monotone" dataKey="nitro_multiple" stroke="#3b82f6" strokeWidth={2} name="Nitro" dot={{ r: 3, fill: '#3b82f6' }} />
                            <Line yAxisId="right" type="monotone" dataKey="comps_multiple" stroke="#ef4444" strokeWidth={2} name="Comps" dot={{ r: 3, fill: '#ef4444' }} strokeDasharray="5 5" />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>
                      {tradingMultiple.description && (
                        <div className="text-xs text-slate-700 leading-relaxed">
                          <p className="whitespace-pre-wrap">{tradingMultiple.description}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-600">Trading multiple evolution chart will be displayed here</p>
                  )}
                </div>
              </>
            );
          })()}
        </A4Page>

        {/* ── PAGE 5 – Background / Valuation Review / Commentary ── */}
        <A4Page pageNum={5}>
          {(() => {
            const page3 = reportData.page_3 || {};
            const background = page3.background || {};
            const valuationReview = page3.valuation_review || {};
            const tradingCommentary = page3.trading_multiple_commentary || {};

            return (
              <>
                <PageHeader />

                <div className="mb-6">
                  <h2 className="text-base font-bold mb-3" style={{ color: '#A8A67A' }}>
                    {background.title || 'Background'}
                  </h2>
                  {background.content && background.content.length > 0 ? (
                    <ul className="space-y-2 text-xs text-slate-700">
                      {background.content.map((point, idx) => (
                        <li key={idx} className="flex gap-2">
                          <span className="text-slate-400 mt-0.5">•</span>
                          <span className="flex-1 leading-relaxed">{point}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-slate-600">Background information will be displayed here</p>
                  )}
                </div>

                <div className="mb-6">
                  <h2 className="text-base font-bold mb-3" style={{ color: '#A8A67A' }}>
                    {valuationReview.title || 'Valuation Review'}
                  </h2>
                  {valuationReview.content && valuationReview.content.length > 0 ? (
                    <>
                      <ul className="space-y-2 text-xs text-slate-700 mb-3">
                        {valuationReview.content.map((point, idx) => (
                          <li key={idx} className="flex gap-2">
                            <span className="text-slate-400 mt-0.5">•</span>
                            <span className="flex-1 leading-relaxed">{point}</span>
                          </li>
                        ))}
                      </ul>
                      {valuationReview.footnotes && valuationReview.footnotes.length > 0 && (
                        <div className="mt-4 pt-3 border-t border-slate-200">
                          {valuationReview.footnotes.map((footnote, idx) => (
                            <p key={idx} className="text-xs text-slate-500 mb-1.5 italic">{footnote}</p>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-xs text-slate-600">Valuation review will be displayed here</p>
                  )}
                </div>

                <div>
                  <h2 className="text-base font-bold mb-3" style={{ color: '#A8A67A' }}>
                    {tradingCommentary.title || 'Trading Multiple Evolution'}
                  </h2>
                  {tradingCommentary.content && tradingCommentary.content.length > 0 ? (
                    <ul className="space-y-2 text-xs text-slate-700">
                      {tradingCommentary.content.map((point, idx) => (
                        <li key={idx} className="flex gap-2">
                          <span className="text-slate-400 mt-0.5">•</span>
                          <span className="flex-1 leading-relaxed">{point}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-slate-600">Trading multiple commentary will be displayed here</p>
                  )}
                </div>
              </>
            );
          })()}
        </A4Page>

        {/* ── PAGE 6 – Assessment of Fair Value ── */}
        <A4Page pageNum={6}>
          {(() => {
            const bullets = assessmentFV.bullets || [];
            return (
              <>
                <PageHeader />
                <div>
                  <h2 className="text-base font-bold mb-3" style={{ color: '#A8A67A' }}>
                    Assessment of Fair Value
                  </h2>
                  {bullets.length > 0 ? (
                    <div className="space-y-3">
                      {bullets.map((bullet, idx) => {
                        const text = bullet.replace(/^[►▶➤>\-•*]+\s*/, '').trim();
                        return (
                          <div key={idx} className="flex gap-2 items-start">
                            <span className="text-xs font-bold mt-0.5 flex-shrink-0" style={{ color: '#A8A67A' }}>►</span>
                            <p className="text-xs leading-relaxed text-slate-700">{text}</p>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 italic">
                      Assessment of Fair Value content will appear here once the report is generated.
                    </p>
                  )}
                </div>
              </>
            );
          })()}
        </A4Page>

        {/* ── PAGE 7 – Review of Operational Factors ── */}
        <A4Page pageNum={7}>
          {(() => {
            const bullets = reviewOF.bullets || [];
            const intro = reviewOF.intro || '';
            const perfBaseCase = reviewOF.performance_vs_base_case || '';

            return (
              <>
                <PageHeader />

                <div>
                  <h2 className="text-base font-bold mb-3" style={{ color: '#A8A67A' }}>
                    Review of Operational Factors
                  </h2>

                  {intro ? (
                    <p className="text-xs text-slate-700 mb-4 leading-relaxed">{intro}</p>
                  ) : null}

                  {bullets.length > 0 ? (
                    <div className="space-y-2">
                      {bullets.map((bullet, idx) => {
                        const hasSubItems = bullet.sub_items && bullet.sub_items.length > 0;
                        return (
                          <div key={idx} className="flex gap-2 items-start">
                            <span className="text-xs font-bold mt-0.5 flex-shrink-0" style={{ color: '#A8A67A' }}>►</span>
                            <div className="flex-1">
                              {bullet.title ? (
                                <span>
                                  <span className="text-xs font-bold underline">{bullet.title}</span>
                                  {bullet.text ? (
                                    <span className="text-xs text-slate-700 leading-relaxed"> – {bullet.text}</span>
                                  ) : null}
                                </span>
                              ) : (
                                <span className="text-xs text-slate-700 leading-relaxed">{bullet.text}</span>
                              )}

                              {hasSubItems && (
                                <ol className="mt-1.5 space-y-1 list-none pl-0">
                                  {bullet.sub_items.map((sub, sidx) => (
                                    <li key={sidx} className="flex gap-2 text-xs text-slate-700 leading-relaxed">
                                      <span className="font-semibold flex-shrink-0">{sidx + 1}.</span>
                                      <span>
                                        {sub.label ? (
                                          <><span className="font-bold">{sub.label}</span>{' '}</>
                                        ) : null}
                                        {sub.text}
                                      </span>
                                    </li>
                                  ))}
                                </ol>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 italic">
                      Review of Operational Factors content will appear here once the report is generated.
                    </p>
                  )}

                  {perfBaseCase ? (
                    <div className="mt-4 pt-3 border-t border-slate-300">
                      <p className="text-xs text-slate-700 leading-relaxed">
                        <span className="font-bold underline">Performance Versus Initial Base Case</span>
                        {' – '}{perfBaseCase.replace(/^Performance Versus Initial Base Case\s*[–\-:]\s*/i, '')}
                      </p>
                    </div>
                  ) : null}
                </div>
              </>
            );
          })()}
        </A4Page>

        {/* ── PAGE 8 – Financial Performance ── */}
        <A4Page pageNum={8}>
          {(() => {
            const fpBullets = financialPerf.bullets || [];
            const fpIntro = financialPerf.intro || '';

            return (
              <>
                <PageHeader />

                <div>
                  <h2 className="text-base font-bold mb-3" style={{ color: '#A8A67A' }}>
                    Financial Performance
                  </h2>

                  {fpIntro ? (
                    <p className="text-xs text-slate-700 mb-4 leading-relaxed">{fpIntro}</p>
                  ) : null}

                  {fpBullets.length > 0 ? (
                    <div className="space-y-2 mb-6">
                      {fpBullets.map((bullet, idx) => (
                        <div key={idx} className="flex gap-2 items-start">
                          <span className="text-xs font-bold mt-0.5 flex-shrink-0" style={{ color: '#A8A67A' }}>►</span>
                          <div className="flex-1">
                            <p className="text-xs text-slate-700 leading-relaxed font-medium">{bullet.text}</p>
                            {bullet.sub_bullets && bullet.sub_bullets.length > 0 && (
                              <ul className="mt-1.5 space-y-0.5 pl-2">
                                {bullet.sub_bullets.map((sub, sidx) => (
                                  <li key={sidx} className="flex gap-2 text-xs text-slate-700 leading-relaxed">
                                    <span className="flex-shrink-0 mt-0.5">○</span>
                                    <span>{sub}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 italic mb-4">
                      Financial Performance content will appear here once the report is generated.
                    </p>
                  )}

                  {/* Financials screenshot table image */}
                  {financialsImageUrl && (
                    <div className="mt-4 border border-slate-200 rounded-lg overflow-hidden">
                      <img
                        src={financialsImageUrl}
                        alt="Financial Projections Table"
                        className="w-full object-contain"
                        style={{ maxHeight: '400px' }}
                      />
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </A4Page>

        {/* ── PAGE 9 – Valuation Summary ── */}
        <A4Page pageNum={9}>
          {(() => {
            const vsBullets = valuationSummary.bullets || [];
            const chartData = valuationSummary.chart_data || [];
            // Support both current field name (average_ev) and legacy field name (selected_ev)
            const averageEV = valuationSummary.average_ev
              ? Number(valuationSummary.average_ev)
              : (valuationSummary.selected_ev ? Number(valuationSummary.selected_ev) : null);
            const averageEVLabel = valuationSummary.average_ev_label
              || valuationSummary.selected_ev_label
              || (averageEV ? `$${averageEV}m` : '');

            // Football field chart — pure SVG, no extra deps
            const FootballFieldChart = () => {
              if (!chartData.length) return (
                <p className="text-xs text-slate-500 italic text-center py-8">
                  Football field chart will appear once report is generated with Excel data.
                </p>
              );

              const allNums = chartData.flatMap(d => [Number(d.low), Number(d.high), Number(d.point)]);
              if (averageEV) allNums.push(averageEV);
              const domMin = Math.floor(Math.min(...allNums) * 0.88);
              const domMax = Math.ceil(Math.max(...allNums) * 1.08);

              const SVG_W = 560;
              const LABEL_W = 120;
              const RIGHT_PAD = 50;
              const PLOT_W = SVG_W - LABEL_W - RIGHT_PAD;
              const ROW_H = 48;
              const BAR_H = 18;
              const TOP_PAD = 30;
              const BOTTOM_PAD = 36;
              const SVG_H = chartData.length * ROW_H + TOP_PAD + BOTTOM_PAD;

              const toX = (val) => LABEL_W + ((Number(val) - domMin) / (domMax - domMin)) * PLOT_W;

              // X-axis ticks
              const tickCount = 6;
              const tickStep = Math.ceil((domMax - domMin) / tickCount / 50) * 50;
              const ticks = [];
              for (let v = Math.ceil(domMin / tickStep) * tickStep; v <= domMax; v += tickStep) ticks.push(v);

              return (
                <div>
                  <p className="text-center text-sm font-bold mb-2" style={{ color: '#2563eb' }}>
                    Enterprise Value {averageEVLabel}
                  </p>
                  <svg width="100%" viewBox={`0 0 ${SVG_W} ${SVG_H}`} style={{ fontFamily: 'inherit' }}>
                    {/* Grid lines */}
                    {ticks.map(t => (
                      <line key={t} x1={toX(t)} y1={TOP_PAD - 8} x2={toX(t)} y2={SVG_H - BOTTOM_PAD}
                        stroke="#e2e8f0" strokeWidth={1} />
                    ))}

                    {/* Bars for each method */}
                    {chartData.map((d, i) => {
                      const y = TOP_PAD + i * ROW_H;
                      const barY = y + (ROW_H - BAR_H) / 2;
                      const rangeX1 = toX(d.low);
                      const rangeX2 = toX(d.high);
                      const pointX = toX(d.point);
                      const markerW = Math.max(10, PLOT_W * 0.02);

                      return (
                        <g key={i}>
                          {/* Method label */}
                          <text x={LABEL_W - 8} y={barY + BAR_H / 2 + 4} textAnchor="end"
                            fontSize={10} fill="#475569" fontWeight="500">
                            {d.method}
                          </text>
                          {/* Range bar (light blue) */}
                          <rect x={rangeX1} y={barY} width={rangeX2 - rangeX1} height={BAR_H}
                            fill="#bfdbfe" rx={3} />
                          {/* Point estimate marker (dark blue) */}
                          <rect x={pointX - markerW / 2} y={barY - 5} width={markerW} height={BAR_H + 10}
                            fill="#1d4ed8" rx={3} />
                          {/* Value labels */}
                          <text x={rangeX1 - 3} y={barY + BAR_H / 2 + 4} textAnchor="end"
                            fontSize={9} fill="#64748b">{d.low}</text>
                          <text x={rangeX2 + 3} y={barY + BAR_H / 2 + 4} textAnchor="start"
                            fontSize={9} fill="#64748b">{d.high}</text>
                        </g>
                      );
                    })}

                    {/* Average EV reference line */}
                    {averageEV && (
                      <g>
                        <line x1={toX(averageEV)} y1={TOP_PAD - 12} x2={toX(averageEV)} y2={SVG_H - BOTTOM_PAD + 4}
                          stroke="#0ea5e9" strokeWidth={2} strokeDasharray="6 3" />
                        <text x={toX(averageEV)} y={TOP_PAD - 14} textAnchor="middle"
                          fontSize={10} fill="#0ea5e9" fontWeight="bold">
                          {averageEVLabel}
                        </text>
                      </g>
                    )}

                    {/* X-axis labels */}
                    {ticks.map(t => (
                      <text key={t} x={toX(t)} y={SVG_H - BOTTOM_PAD + 16} textAnchor="middle"
                        fontSize={9} fill="#94a3b8">
                        ${t}m
                      </text>
                    ))}

                    {/* X-axis baseline */}
                    <line x1={LABEL_W} y1={SVG_H - BOTTOM_PAD} x2={SVG_W - RIGHT_PAD} y2={SVG_H - BOTTOM_PAD}
                      stroke="#cbd5e1" strokeWidth={1} />

                    {/* Legend */}
                    <rect x={LABEL_W} y={SVG_H - 16} width={14} height={10} fill="#bfdbfe" rx={2} />
                    <text x={LABEL_W + 18} y={SVG_H - 7} fontSize={9} fill="#64748b">Valuation Range</text>
                    <rect x={LABEL_W + 100} y={SVG_H - 16} width={10} height={10} fill="#1d4ed8" rx={2} />
                    <text x={LABEL_W + 114} y={SVG_H - 7} fontSize={9} fill="#64748b">Point Estimate</text>
                    {averageEV && (
                      <>
                        <line x1={LABEL_W + 220} y1={SVG_H - 11} x2={LABEL_W + 238} y2={SVG_H - 11}
                          stroke="#0ea5e9" strokeWidth={2} strokeDasharray="4 2" />
                        <text x={LABEL_W + 242} y={SVG_H - 7} fontSize={9} fill="#64748b">Average EV</text>
                      </>
                    )}
                  </svg>
                </div>
              );
            };

            return (
              <>
                <PageHeader />

                <div>
                  <h2 className="text-base font-bold mb-3" style={{ color: '#A8A67A' }}>
                    Valuation Summary
                  </h2>

                  {/* Text bullets */}
                  {vsBullets.length > 0 ? (
                    <div className="space-y-2 mb-6">
                      {vsBullets.map((bullet, idx) => (
                        <div key={idx} className="flex gap-2 items-start">
                          <span className="text-xs font-bold mt-0.5 flex-shrink-0" style={{ color: '#A8A67A' }}>►</span>
                          <p className="text-xs text-slate-700 leading-relaxed">{bullet}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 italic mb-4">
                      Valuation Summary text will appear once the report is generated.
                    </p>
                  )}

                  {/* Football field chart */}
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <FootballFieldChart />
                  </div>
                </div>
              </>
            );
          })()}
        </A4Page>

        {/* ── PAGE 10 – Earnings Multiples ── */}
        <A4Page pageNum={10}>
          {(() => {
            const emText = earningsMultiples.text || '';
            const pct = earningsMultiples.peer_comparison_table || {};
            const tableRows = [
              { label: 'Price per Share (A$)', value: pct.price_per_share, bold: false },
              { label: 'No. Shares (# millions)', value: pct.shares_millions, bold: false },
              { label: 'Market Cap (A$m)', value: pct.market_cap, bold: true },
              { label: 'Net Debt (A$m)', value: pct.net_debt, bold: false },
              { label: 'Enterprise Value (A$m)', value: pct.enterprise_value_aud, bold: true },
              { label: 'Enterprise Value (U$m)', value: pct.enterprise_value_usd, bold: true },
              { label: 'NTM Revenue ($m)', value: pct.ntm_revenue, bold: false },
              { label: 'Underlying Multiple (x)', value: pct.underlying_multiple, bold: false },
              { label: 'Listed Multiple Public Comps', value: pct.listed_multiple_public_comps, bold: false },
            ].filter(r => r.value != null);
            const discountRow = pct.discount_to_public_comps;

            return (
              <>
                <PageHeader />
                <div>
                  <h2 className="text-base font-bold mb-3" style={{ color: '#A8A67A' }}>
                    Earnings Multiples
                  </h2>

                  {emText ? (
                    <div className="text-xs text-slate-700 leading-relaxed mb-4 whitespace-pre-wrap">
                      {emText}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 italic mb-4">
                      Earnings Multiples text will appear once the report is generated.
                    </p>
                  )}

                  {tableRows.length > 0 && (
                    <div className="bg-slate-100 p-3 rounded-lg border-2" style={{ borderColor: '#A8A67A' }} data-testid="earnings-multiples-table">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="text-xs font-bold">Valuation - Peer Comparison (AUDm)</h4>
                        <span className="text-xs font-semibold">Nitro Fair Value</span>
                      </div>
                      <table className="w-full text-xs">
                        <tbody>
                          {tableRows.map((row, idx) => (
                            <tr key={idx} className={`border-b${row.bold ? ' bg-white' : ''}`}>
                              <td className={`py-1 ${row.bold ? 'font-semibold' : ''}`}>{row.label}</td>
                              <td className={`text-right ${row.bold ? 'font-bold' : ''}`}>{row.value}</td>
                            </tr>
                          ))}
                          {discountRow && (
                            <tr className="border-t-2 border-slate-600">
                              <td className="py-1 font-bold italic">Discount to Public Comps</td>
                              <td className="text-right font-bold text-sm" style={{ color: '#A8A67A' }}>{discountRow}</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Peer comparison screenshot */}
                  {earningsMultiplesImageUrl && (
                    <div className="mt-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="earnings-multiples-image">
                      <img
                        src={earningsMultiplesImageUrl}
                        alt="Earnings Multiples – Peer Comparison"
                        className="w-full object-contain"
                      />
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </A4Page>





      </div>
    </div>
  );
};

export default NitroReportViewer;
